import { ArrowLeftIcon, ArrowRightIcon, Button, CloseIcon, Header, IconButton, Paragraph } from "@datagame/ui";
import { useEffect, useState } from "react";
import { useMediaQuery } from "react-responsive";
import { useTranslation } from "react-i18next";
import classNames from "classnames/bind";

import { useWordopolis } from "../../../../hooks/useWordopolis";
import { WordopolisActionKind } from "../../../../reducers/wordopolis";
import { WordopolisModal } from "../WordopolisModal";
import swipingImage from "../../../../assets/images/swiping.png";
import timePresureImage from "../../../../assets/images/timePresure.png";
import zenMode from "../../../../assets/images/zenMode.png";
import zoomOutImage from "../../../../assets/images/zoomOut.png";

import styles from "./IntroModal.module.scss";

const cx = classNames.bind(styles);

export type IntroModalSteps = 0 | 1 | 2;

export const IntroModal = () => {
  const [
    {
      configuration: { difficulty, isTimeLimited, gameVisuals, startText },
      status: { isIntroFinished },
    },
    dispatch,
  ] = useWordopolis();
  const { themeColor } = gameVisuals || {};
  const { t } = useTranslation();
  const isDesktop = useMediaQuery({ query: "(min-width: 1280px)" });

  const [currentStep, setCurrentStep] = useState<IntroModalSteps>(isDesktop ? 1 : 0);

  const stepsToRender = [0, 1, 2]
    .map((step) => Number(step))
    .filter((step) => {
      if (isDesktop) return step !== 0;
      return true;
    });

  const isFirstStep = currentStep === stepsToRender[0];
  const isLastStep = currentStep === stepsToRender[stepsToRender.length - 1];

  const handleClose = () => dispatch({ type: WordopolisActionKind.FINISH_INTRO });

  const changeStep = (step: IntroModalSteps | "next" | "previous") => {
    if (step === "next" && isLastStep) {
      return handleClose();
    }

    let nextStep = currentStep;

    switch (step) {
      case "next":
        nextStep++;
        break;
      case "previous":
        nextStep--;
        break;
      default:
        nextStep = step;
    }

    if (!stepsToRender.includes(nextStep)) {
      return;
    }

    setCurrentStep(nextStep as IntroModalSteps);
  };

  useEffect(() => {
    if (isDesktop && currentStep === 0) {
      changeStep(1);
    }
  }, [isDesktop]);

  const introImage = [zoomOutImage, swipingImage, isTimeLimited ? timePresureImage : zenMode][currentStep];

  const introHeader = [
    t("wordopolis.modals.intro.step0.header"),
    t(`wordopolis.modals.intro.step1.header.${difficulty}`),
    t("wordopolis.modals.intro.step2.header"),
  ][currentStep];

  const introDescription = [
    t("wordopolis.modals.intro.step0.description"),
    t(`wordopolis.modals.intro.step1.description.${difficulty}`),
    t(`wordopolis.modals.intro.step2.description.${isTimeLimited ? "time" : "zen"}`),
  ][currentStep];

  return (
    <WordopolisModal isVisible={!isIntroFinished} className={styles.modal}>
      <div className={styles.controlPanel}>
        <IconButton size="small" icon={<CloseIcon />} onClick={handleClose} />
        {!isFirstStep && <IconButton size="small" onClick={() => changeStep("previous")} icon={<ArrowLeftIcon />} />}
      </div>

      <img className={styles.image} src={introImage} alt="intro" />

      <div className={styles.steps}>
        {stepsToRender.map((index) => (
          <button
            key={index}
            style={{ backgroundColor: index === currentStep ? themeColor : undefined }}
            className={cx("step", { active: index === currentStep })}
            onClick={() => changeStep(index as IntroModalSteps)}
          />
        ))}
      </div>

      <Header className={styles.header} variant="h6">
        {introHeader}
      </Header>
      <Paragraph size="small" color="secondary" className={styles.paragraph}>
        {introDescription}
      </Paragraph>

      <div className={styles.bottom}>
        <Button customColor={themeColor} onClick={() => changeStep("next")} icon={<ArrowRightIcon />} iconSide="left">
          {isLastStep ? startText : t(`wordopolis.modals.intro.nextStep`)}
        </Button>
        <Button customColor={themeColor} onClick={handleClose} kind="text">
          {t("skip")}
        </Button>
      </div>
    </WordopolisModal>
  );
};
